import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { hideAccidentPremiums } from 'Containers/App/utils';
import {
  ALL_NONSELECTABLE_PLAN_TYPES,
  CONTENT_MAP,
  CUSTOM_NAME_PLAN_TYPES,
  NO_SET_PREMIUMS_PLAN_TYPES,
  SET_PREMIUM_PLAN_TYPES,
} from 'Containers/OverviewPage/constants';
import { InsuranceType } from 'Containers/ViewBenefitPage/types';
import { ContentfulType } from 'ContentfulDefaults/ContentfulType';
import { ContentfulSupplementalPlan } from 'ContentfulDefaults/types/benefitsAdditional';
import { usePremiumContext } from 'Contexts/premiumContext';
import { useTextContext } from 'Contexts/textContext';
import { NumberWithPayPeriod } from 'DesignLibrary/atoms';
import { Button } from 'DesignLibrary/atoms/Button';
import { PremiumPerDeduction } from 'DesignLibrary/atoms/PremiumPerDeduction';
import Text from 'Shared/Text';
import {
  Recommendation,
  SelectedSupplementalPlans,
  SupplementalPlan,
  SupplementalPlansFullHousehold,
} from 'Types/entities';
import { BuilderGoSettings, CdnModuleConfig } from 'Utils/apiTypes';
import Logger from 'Utils/logger';

import Card from './Card';
import { ReviewGroup } from './ReviewGroup';
import { Container, ShowMoreButton } from './styled';

// Using SELECTABLE_PLAN_TYPES constant while removing HSA and HRA
const remove = ['hsa', 'hra'];
const REVIEW_NONSELECTABLE_PLAN_TYPES = ALL_NONSELECTABLE_PLAN_TYPES.filter((item) => !remove.includes(item));

export interface ReviewSectionProps {
  healthPlan: Recommendation | Record<string, never>;
  hsaPlan: SupplementalPlan | null;
  hraPlan: SupplementalPlan | null;
  sihraContribution: number;
  supplementalPlans: SupplementalPlansFullHousehold;
  selectedSupplementalPlans: SelectedSupplementalPlans;
  supplementalPremiums: number;
  recommendedHsaContribution: number;
  isFbsEnabled: boolean;
  handleSupplementalChange: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleHealthChange: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleHsaChange: (e: React.MouseEvent<HTMLButtonElement>) => void;
  builderGoSettings: BuilderGoSettings | null;
  dataRevisionLocation: string | null;
}

const ReviewSection = ({
  healthPlan,
  hsaPlan,
  hraPlan,
  sihraContribution = 0,
  supplementalPlans,
  selectedSupplementalPlans,
  supplementalPremiums,
  recommendedHsaContribution,
  isFbsEnabled,
  handleSupplementalChange,
  handleHealthChange,
  handleHsaChange,
  builderGoSettings,
  dataRevisionLocation,
}: ReviewSectionProps) => {
  const [showEdu, setShowEdu] = useState(false);

  const { retrieveContentfulData } = useTextContext();
  const { useContributionTerm } = usePremiumContext();

  const [cdnFetchFailed, setCdnFetchFailed] = useState(false);
  const [cdnFetchLoading, setCdnFetchLoading] = useState(!!dataRevisionLocation);
  const [cdnModuleState, setCdnModuleState] = useState({
    externalIdToModuleKey: {} as Record<string, string>,
    moduleKeyToTitle: {} as Record<string, string>,
    allCdnExternalIds: new Set<string>(),
  });
  const { externalIdToModuleKey, moduleKeyToTitle, allCdnExternalIds } = cdnModuleState;

  useEffect(() => {
    const controller = new AbortController();
    if (dataRevisionLocation) {
      setCdnFetchFailed(false);
      setCdnFetchLoading(true);
      setCdnModuleState({
        externalIdToModuleKey: {},
        moduleKeyToTitle: {},
        allCdnExternalIds: new Set(),
      });
      fetch(dataRevisionLocation, { signal: controller.signal })
        .then((res) => (res.ok ? res.json() : undefined))
        .then((config: Record<string, CdnModuleConfig> | undefined) => {
          const idToModuleKey: Record<string, string> = {};
          const keyToTitle: Record<string, string> = {};
          const allCdnExternalIdsSet = new Set<string>();
          if (config) {
            Object.entries(config).forEach(([key, module]) => {
              if (!key.startsWith('custom_module_')) return;
              (module.plan ?? []).forEach((plan) => allCdnExternalIdsSet.add(String(plan.id)));
              if (module?.settings?.has_details !== true) return;
              if (module.title) keyToTitle[key] = module.title;
              (module.plan ?? []).forEach((plan) => {
                // ?.trim() !== '' allows undefined (field absent) through but excludes empty or whitespace-only strings
                if (plan.details?.trim() !== '') idToModuleKey[String(plan.id)] = key;
              });
            });
          }
          setCdnModuleState({
            externalIdToModuleKey: idToModuleKey,
            moduleKeyToTitle: keyToTitle,
            allCdnExternalIds: allCdnExternalIdsSet,
          });
          setCdnFetchLoading(false);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            Logger.error('[ReviewSection] Failed to fetch CDN config', {
              url: dataRevisionLocation,
              error: err.message,
            });
            setCdnFetchFailed(true);
            setCdnFetchLoading(false);
          }
        });
    }
    return () => {
      controller.abort();
    };
  }, [dataRevisionLocation]);

  const isHealthPlanSelected = !isEmpty(healthPlan);
  const isHsaEligible = isHealthPlanSelected ? healthPlan.plan.hsa_eligible : false;
  const isHraEligible = isHealthPlanSelected ? healthPlan.plan.hra_eligible : false;

  const contentfulSupplementalPlans = retrieveContentfulData<ContentfulSupplementalPlan[]>(
    'benefits_section_additional.supplemental_plans',
    [],
  );

  const externalIdToPlanTitle = useMemo(
    () =>
      contentfulSupplementalPlans.reduce<Record<string, string>>((acc, plan) => {
        if (plan.plan_id && plan.plan_title) acc[plan.plan_id] = plan.plan_title;
        return acc;
      }, {}),
    [contentfulSupplementalPlans],
  );

  const customModuleGroups = useMemo(() => {
    const groups = new Map<string, SupplementalPlan[]>();
    (supplementalPlans?.Other?.plans ?? []).forEach((plan) => {
      const moduleKey = externalIdToModuleKey[plan.external_id];
      if (moduleKey) {
        groups.set(moduleKey, [...(groups.get(moduleKey) ?? []), plan]);
      }
    });
    return groups;
  }, [supplementalPlans?.Other?.plans, externalIdToModuleKey]);

  const resolveModuleTitle = (moduleKey: string, plansForModule: SupplementalPlan[]): string => {
    const contentfulTitle = plansForModule.map((p) => externalIdToPlanTitle[p.external_id]).find(Boolean);
    return contentfulTitle ?? moduleKeyToTitle[moduleKey] ?? moduleKey;
  };

  const planTypes = Object.keys(supplementalPlans);
  const selectedPlanTypes = Object.entries(selectedSupplementalPlans);
  const annualHealthPremiums = isHealthPlanSelected ? healthPlan.costs.annual_premium : 0;
  const annualPremiumTotal = annualHealthPremiums + supplementalPremiums * 12;

  const addCarrier = supplementalPlans.life?.plans[0]?.carrier_name || '';

  const getPerDeductionHeader = (annualValue: number, deductions: number) => (
    <PremiumPerDeduction
      annualPremium={annualValue}
      deductions={deductions}
      currency
      size="small"
      fontWeight="bold"
    />
  );

  // legacy header
  const getHeader = (
    value: number,
    hideZeroValue: boolean = false,
    payPeriodType: 'pay_period' | 'yearly' = 'pay_period',
  ): React.ReactNode =>
    value === 0 && hideZeroValue ? null : (
      <div className="value">
        <NumberWithPayPeriod value={value} size="small" fontWeight="bold" payPeriodType={payPeriodType} />
      </div>
    );

  // If number of deductions differ, disable the total atop the premiums card
  const deductions = Object.values(selectedSupplementalPlans)
    .filter((p) => p !== null && p !== undefined && p.deductions_per_year > 0)
    .map((p) => p?.deductions_per_year);
  if (healthPlan?.costs?.annual_premium_deductions) {
    deductions.push(healthPlan.costs.annual_premium_deductions);
  }
  const deductionsSet = [...new Set(deductions)];

  const premiumHeader =
    deductionsSet.length === 1 ? getPerDeductionHeader(annualPremiumTotal, deductionsSet[0] as number) : null;

  const hsaHeader = healthPlan?.costs?.annual_premium_deductions
    ? getPerDeductionHeader(recommendedHsaContribution, healthPlan.costs.annual_premium_deductions)
    : getHeader(recommendedHsaContribution / 12);

  const setPremiumPlans = selectedPlanTypes.filter(
    ([planType, benefit]) => benefit && SET_PREMIUM_PLAN_TYPES.includes(planType as InsuranceType),
  ) as [InsuranceType, SupplementalPlan][];

  const noSetPremiumPlans = selectedPlanTypes.filter(
    ([planType, benefit]) => benefit && NO_SET_PREMIUMS_PLAN_TYPES.includes(planType as InsuranceType),
  ) as [InsuranceType, SupplementalPlan][];

  const noPremiumPlans = planTypes
    .filter((planType) => REVIEW_NONSELECTABLE_PLAN_TYPES.includes(planType as InsuranceType))
    .map((planType) => {
      if (planType === 'Other') {
        // While loading or on CDN failure, Other plans are rendered individually via cdnFailurePlans below
        if (cdnFetchFailed || cdnFetchLoading) return [planType, undefined];
        return [
          planType,
          (supplementalPlans.Other?.plans ?? []).find((p) => !allCdnExternalIds.has(p.external_id)),
        ];
      }
      return [planType, supplementalPlans[planType]?.plans[0]];
    })
    .filter(([, plan]) => plan != null) as [InsuranceType, SupplementalPlan][];

  const cdnFailurePlans = useMemo(
    () => (cdnFetchFailed || cdnFetchLoading ? (supplementalPlans?.Other?.plans ?? []) : []),
    [cdnFetchFailed, cdnFetchLoading, supplementalPlans?.Other?.plans],
  );

  const showOtherBenefits =
    isFbsEnabled &&
    noSetPremiumPlans.length + noPremiumPlans.length + customModuleGroups.size + cdnFailurePlans.length > 0;

  const showMoreBenefitsButton =
    noSetPremiumPlans.length + noPremiumPlans.length + customModuleGroups.size + cdnFailurePlans.length > 5;

  const headerTooltips = retrieveContentfulData<ContentfulType['review_section']['card_header_tooltips']>(
    'review_section.card_header_tooltips',
  );

  const employerPaidPlanIds = contentfulSupplementalPlans
    .map((i) => {
      if (i.is_employer_paid) {
        return i.plan_id;
      }
      return null;
    })
    .filter((i) => i);

  const getIsAutoEnrolled = (planType) =>
    supplementalPlans[planType]?.plans?.some((i) => i.auto_enrolled) || false;

  const handleShowEdu = () => {
    setShowEdu((prev) => !prev);
  };

  const benefitName = (planType, custom_name) =>
    (CUSTOM_NAME_PLAN_TYPES.includes(planType as InsuranceType) && custom_name
      ? custom_name
      : retrieveContentfulData<string>(CONTENT_MAP[planType].title)) || planType;

  const carrierName = (benefit: SupplementalPlan) =>
    benefit.plan_type === 'add' ? addCarrier : benefit.carrier_name;

  const getHealthPlanName = (planType: InsuranceType, benefit: SupplementalPlan): JSX.Element | string =>
    isHsaEligible && (planType === 'fsa' || planType === 'fsa_medical') ? (
      <Text field={CONTENT_MAP[planType].titleAlt} />
    ) : (
      benefitName(planType, benefit.name)
    );

  const premGroupTitleField = useContributionTerm
    ? 'review_section.benefit_contributions'
    : 'review_section.card_header_tooltips.premiums.title';

  return (
    <Container>
      <ReviewGroup
        title={<Text field={premGroupTitleField} />}
        tooltip={<Text field="review_section.card_header_tooltips.premiums.text" />}
        header={premiumHeader}
      >
        {healthPlan.plan ? (
          <Card
            planType="health"
            carrier={healthPlan.plan.carrier_name}
            healthPlanName={healthPlan.plan.plan_marketing_name}
            premium={healthPlan.costs.effective_premium}
            annualPremium={healthPlan.costs.annual_premium}
            deductionsPerYear={healthPlan.costs.annual_premium_deductions}
            handleChangeButton={handleHealthChange}
          />
        ) : (
          <Card
            planType="waived_coverage"
            carrier="Waived"
            healthPlanName="No Health Plan"
            // TODO: add `waived` to contentful for Spanish translation
            handleChangeButton={handleHealthChange}
          />
        )}
        {setPremiumPlans.length > 0 &&
          setPremiumPlans.map(([planType, benefit]) => (
            <Card
              key={benefit.plan_type}
              planType={planType}
              carrier={benefit.carrier_name}
              healthPlanName={benefitName(planType, benefit.name)}
              premium={benefit.monthly_premium?.data as number}
              annualPremium={(benefit.monthly_premium?.data as number) * 12}
              deductionsPerYear={benefit.deductions_per_year}
              handleChangeButton={handleSupplementalChange}
              isEmployerPaid={employerPaidPlanIds.includes(benefit.external_id)}
              isAccidentPremiumHidden={hideAccidentPremiums(benefit, builderGoSettings)}
            />
          ))}
      </ReviewGroup>

      {isHsaEligible && (
        <ReviewGroup
          title={headerTooltips.hsa_contributions.title}
          tooltip={headerTooltips.hsa_contributions.text}
          header={hsaHeader}
        >
          <Card
            planType="hsa"
            carrier={hsaPlan ? hsaPlan.carrier_name : ''}
            healthPlanName={hsaPlan ? hsaPlan.name : 'HSA'}
            premium={recommendedHsaContribution / 12}
            annualPremium={recommendedHsaContribution}
            deductionsPerYear={healthPlan.costs.annual_premium_deductions}
            handleChangeButton={handleHsaChange}
          />
        </ReviewGroup>
      )}

      {isHraEligible && (
        <ReviewGroup
          title={headerTooltips.hra_contributions.title}
          tooltip={headerTooltips.hra_contributions.text}
          header={getHeader(0, false)}
        >
          <Card
            planType="hra"
            carrier={hraPlan ? hraPlan.carrier_name : ''}
            healthPlanName={hraPlan ? hraPlan.name : 'HRA'}
            // TODO: add `HRA` to contentful for Spanish translation
            premium={0}
            handleChangeButton={handleHsaChange}
          />
        </ReviewGroup>
      )}

      {sihraContribution > 0 && (
        <ReviewGroup
          title={headerTooltips.employer_contributions.title}
          tooltip={headerTooltips.employer_contributions.text}
          header={getHeader(
            // DO WE WANT ONE OR THE OTHER, OR TAKE THE SUM?
            sihraContribution ||
              healthPlan.plan.spending_account_contributions.hra_annual.employer_contribution_amount ||
              0,
            false,
            'yearly',
          )}
        >
          <Card
            planType="hra"
            carrier={hraPlan ? hraPlan.carrier_name : ''}
            healthPlanName={hraPlan ? hraPlan.name : 'HRA'}
            // TODO: add `HRA` to contentful for Spanish translation
            premium={0}
          />
        </ReviewGroup>
      )}

      {showOtherBenefits && (
        <ReviewGroup
          title={headerTooltips.other_benefits.title}
          tooltip={headerTooltips.other_benefits.text}
          header={getHeader(0, true)}
        >
          {noSetPremiumPlans.length > 0 &&
            noSetPremiumPlans.map(([planType, benefit]) => (
              <Card
                key={planType}
                planType={planType}
                carrier={carrierName(benefit)}
                healthPlanName={getHealthPlanName(planType, benefit)}
                premium={benefit.monthly_premium?.data as number}
                handleChangeButton={handleSupplementalChange}
                autoEnrolled={getIsAutoEnrolled(planType)}
                planCount={supplementalPlans[planType]?.plans.length}
                isEmployerPaid={employerPaidPlanIds.includes(benefit.external_id)}
              />
            ))}
          <div
            className={`${showEdu || !showMoreBenefitsButton ? 'showEdu' : 'hideEdu'} additional-benefits`}
          >
            {noPremiumPlans.length > 0 &&
              noPremiumPlans.map(([planType, benefit]) => {
                let planTitle: JSX.Element | string = benefit.name;

                if (supplementalPlans[benefit.plan_type].plans.length <= 1) {
                  planTitle = benefitName(planType, benefit.name);
                }
                if (
                  // Display plan types rather than plan names for non-selectable benefits if there is more than 1 available plan
                  isHsaEligible &&
                  (benefit.plan_type === 'fsa' || benefit.plan_type === 'fsa_medical')
                ) {
                  // Display alternate title for FSA when health plan is HSA eligible
                  planTitle = <Text field={CONTENT_MAP[benefit.plan_type].titleAlt} />;
                } else {
                  // Display data-driven names when there are several selectable benefits
                  planTitle = <Text field={CONTENT_MAP[benefit.plan_type].title} />;
                }

                return (
                  <Card
                    key={benefit.plan_type}
                    planType={benefit.plan_type as InsuranceType}
                    carrier={benefit.carrier_name}
                    healthPlanName={planTitle}
                    premium={benefit.monthly_premium?.data as number}
                    autoEnrolled={getIsAutoEnrolled(planType)}
                    planCount={supplementalPlans[planType]?.plans.length}
                  />
                );
              })}
            {Array.from(customModuleGroups.entries()).map(([moduleKey, plansForModule]) => (
              <Card
                key={moduleKey}
                // custom_module_* keys are not valid InsuranceType values but are safe here:
                // SupplementalIcon falls back to 'other' for unrecognized types.
                planType={moduleKey as InsuranceType}
                carrier={plansForModule[0]?.carrier_name ?? ''}
                healthPlanName={resolveModuleTitle(moduleKey, plansForModule)}
                premium={plansForModule[0]?.monthly_premium?.data as number}
                autoEnrolled={plansForModule.some((p) => p.auto_enrolled)}
                planCount={plansForModule.length}
              />
            ))}
            {cdnFailurePlans.map((plan) => (
              <Card
                key={plan.external_id}
                planType={'Other' as InsuranceType}
                carrier={plan.carrier_name}
                healthPlanName={plan.name}
                premium={plan.monthly_premium?.data as number}
                autoEnrolled={plan.auto_enrolled}
                planCount={1}
              />
            ))}
          </div>
          {showMoreBenefitsButton && (
            <ShowMoreButton className="no-print">
              <Button
                stretch
                size="small"
                buttonType="transparent"
                onClick={handleShowEdu}
                iconRight={showEdu ? 'CaretUp' : 'CaretDown'}
              >
                {showEdu ? 'Hide' : 'Show All'}
              </Button>
            </ShowMoreButton>
          )}
        </ReviewGroup>
      )}
    </Container>
  );
};

export default ReviewSection;
